import type { Cue, Out } from "./engine.ts";

/**
 * DSP building blocks. Every sound in the game is layered from these three grammatical parts:
 *   ink       – a transient of a few milliseconds that makes the sound readable (click, crack)
 *   color     – the body that carries identity (pitched tone, FM metal, filtered noise)
 *   halftone  – grain and tail (grit, crackle, the Foundry's reverb)
 * Each primitive schedules nodes into an Out and returns how long it rings (seconds after o.t).
 */

// ------------------------------------------------------------------ noise

type Color = "white" | "pink" | "brown" | "crackle";
type Bank = Record<Color, AudioBuffer>;
// Keyed by sample rate: buffers are plain data and can be shared across contexts (the audio lab
// renders hundreds of offline contexts).
const banks = new Map<number, Bank>();

export function bank(ctx: BaseAudioContext): Bank {
  const hit = banks.get(ctx.sampleRate);
  if (hit) return hit;
  const len = ctx.sampleRate * 2;
  const xf = Math.floor(ctx.sampleRate * 0.05);
  // Fill len + xf samples, then fold the overhang back over the head so the buffer loops seamlessly
  // (brown noise drifts, and a hard seam would thump every two seconds inside a loop).
  const make = (fill: (d: Float32Array) => void) => {
    const raw = new Float32Array(len + xf);
    fill(raw);
    let mean = 0;
    for (let i = 0; i < raw.length; i++) mean += raw[i]!;
    mean /= raw.length;
    const b = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = raw[i]! - mean;
    for (let i = 0; i < xf; i++) {
      const w = i / xf;
      d[i] = (raw[i]! - mean) * w + (raw[len + i]! - mean) * (1 - w);
    }
    let peak = 0;
    for (let i = 0; i < len; i++) peak = Math.max(peak, Math.abs(d[i]!));
    if (peak > 0) for (let i = 0; i < len; i++) d[i]! /= peak / 0.9;
    return b;
  };
  const b: Bank = {
    white: make((d) => {
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }),
    // Paul Kellet's pink filter.
    pink: make((d) => {
      let b0 = 0,
        b1 = 0,
        b2 = 0,
        b3 = 0,
        b4 = 0,
        b5 = 0,
        b6 = 0;
      for (let i = 0; i < d.length; i++) {
        const w = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + w * 0.0555179;
        b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.969 * b2 + w * 0.153852;
        b3 = 0.8665 * b3 + w * 0.3104856;
        b4 = 0.55 * b4 + w * 0.5329522;
        b5 = -0.7616 * b5 - w * 0.016898;
        d[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362;
        b6 = w * 0.115926;
      }
    }),
    brown: make((d) => {
      let x = 0;
      for (let i = 0; i < d.length; i++) {
        x = (x + 0.02 * (Math.random() * 2 - 1)) / 1.02;
        d[i] = x;
      }
    }),
    // Sparse grains: debris, grit, sparks.
    crackle: make((d) => {
      for (let i = 0; i < d.length; i++) {
        if (Math.random() < 0.0016) {
          const a = (Math.random() * 2 - 1) * (0.3 + Math.random() * 0.7);
          for (let j = 0; j < 40 && i + j < d.length; j++) d[i + j]! += a * Math.exp(-j / 6) * (j % 2 ? -1 : 1);
        }
      }
    }),
  };
  banks.set(ctx.sampleRate, b);
  return b;
}

// ------------------------------------------------------------------ envelopes

/** Linear attack to `peak`, then an exponential fall that reaches about -43 dB after `d`. */
export function perc(p: AudioParam, t: number, peak: number, a: number, d: number) {
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(peak, t + a);
  p.setTargetAtTime(0, t + a, d / 5);
}

/** Attack, decay to sustain, hold until `t + hold`, release. */
export function adsr(p: AudioParam, t: number, peak: number, a: number, d: number, s: number, hold: number, r: number) {
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(peak, t + a);
  p.setTargetAtTime(peak * s, t + a, d / 3);
  p.setTargetAtTime(0, t + Math.max(hold, a + 0.005), r / 4);
}

// ------------------------------------------------------------------ routing helpers

export const later = (o: Out, s: number): Out => ({ ...o, t: o.t + s });

export function gainOut(o: Out, g: number): Out {
  const n = o.ctx.createGain();
  n.gain.value = g;
  n.connect(o.dest);
  return { ...o, dest: n };
}

export function filterOut(o: Out, type: BiquadFilterType, f: number, q = 0.7): Out {
  const n = o.ctx.createBiquadFilter();
  n.type = type;
  n.frequency.value = f;
  n.Q.value = q;
  n.connect(o.dest);
  return { ...o, dest: n };
}

export function panOut(o: Out, pan: number): Out {
  const n = o.ctx.createStereoPanner();
  n.pan.value = Math.max(-1, Math.min(1, pan));
  n.connect(o.dest);
  return { ...o, dest: n };
}

const curves = new Map<string, Float32Array<ArrayBuffer>>();
function curve(key: string, fn: (x: number) => number) {
  let c = curves.get(key);
  if (!c) {
    c = new Float32Array(1024);
    for (let i = 0; i < 1024; i++) c[i] = fn((i / 1023) * 2 - 1);
    curves.set(key, c);
  }
  return c;
}

/** Saturation: tanh drive with gain compensation. */
export function drive(o: Out, amount: number): Out {
  const n = o.ctx.createWaveShaper();
  const k = Math.max(1, amount);
  n.curve = curve(`d${k}`, (x) => Math.tanh(x * k) / Math.tanh(k));
  n.oversample = "2x";
  n.connect(o.dest);
  return { ...o, dest: n };
}

/** Halftone grit: quantize amplitude into a few steps, like a coarse screen print. */
export function grit(o: Out, steps: number): Out {
  const n = o.ctx.createWaveShaper();
  n.curve = curve(`g${steps}`, (x) => Math.round(x * steps) / steps);
  n.connect(o.dest);
  return { ...o, dest: n };
}

/** A fixed gain trim in dB around a cue (see levels.ts, the mix sheet). */
export const trim =
  (db: number, cue: Cue): Cue =>
  (o) =>
    db === 0 ? cue(o) : cue(gainOut(o, 10 ** (db / 20)));

/** ±pct random variation, so repeated cues never sound machine-identical. */
export const vary = (x: number, pct: number) => x * (1 + (Math.random() * 2 - 1) * pct);
export const semis = (n: number) => 2 ** (n / 12);

// ------------------------------------------------------------------ primitives

export interface NoiseOpts {
  color?: Color;
  type?: BiquadFilterType;
  f?: number;
  /** Filter sweep target. */
  f1?: number;
  q?: number;
  gain: number;
  a?: number;
  d: number;
  delay?: number;
  rate?: number;
}

export function noise(o: Out, n: NoiseOpts): number {
  const { ctx } = o;
  const t = o.t + (n.delay ?? 0);
  const a = n.a ?? 0.002;
  const src = ctx.createBufferSource();
  src.buffer = bank(ctx)[n.color ?? "white"];
  src.playbackRate.value = n.rate ?? 1;
  const f = ctx.createBiquadFilter();
  f.type = n.type ?? "bandpass";
  f.frequency.setValueAtTime(n.f ?? 1000, t);
  if (n.f1) f.frequency.exponentialRampToValueAtTime(n.f1, t + a + n.d * 0.8);
  f.Q.value = n.q ?? (f.type === "bandpass" ? 0.8 : 0);
  const g = ctx.createGain();
  perc(g.gain, t, n.gain, a, n.d);
  src.connect(f).connect(g).connect(o.dest);
  const end = t + a + n.d * 1.3;
  // Random read offset so repeats differ, without running off the end of the 2 s buffer.
  const need = (end - t) * (n.rate ?? 1) + 0.05;
  if (need > 1.9) src.loop = true;
  src.start(t, Math.random() * Math.max(0, 1.95 - need));
  src.stop(end + 0.02);
  return end - o.t;
}

export interface ToneOpts {
  type?: OscillatorType;
  f: number;
  f1?: number;
  /** Time to reach f1 (defaults to the decay). */
  glide?: number;
  gain: number;
  a?: number;
  d: number;
  delay?: number;
  detune?: number;
  /** Optional low-pass on the oscillator. */
  lp?: number;
}

export function tone(o: Out, n: ToneOpts): number {
  const { ctx } = o;
  const t = o.t + (n.delay ?? 0);
  const a = n.a ?? 0.002;
  const osc = ctx.createOscillator();
  osc.type = n.type ?? "sine";
  osc.frequency.setValueAtTime(n.f, t);
  if (n.f1) osc.frequency.exponentialRampToValueAtTime(n.f1, t + (n.glide ?? n.d));
  if (n.detune) osc.detune.value = n.detune;
  const g = ctx.createGain();
  perc(g.gain, t, n.gain, a, n.d);
  let head: AudioNode = osc;
  if (n.lp) {
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = n.lp;
    lp.Q.value = 0;
    osc.connect(lp);
    head = lp;
  }
  head.connect(g).connect(o.dest);
  const end = t + a + n.d * 1.3;
  osc.start(t);
  osc.stop(end + 0.02);
  return end - o.t;
}

export interface FmOpts {
  f: number;
  /** Modulator frequency as a multiple of the carrier: integers ring harmonic, others clang. */
  ratio: number;
  /** Modulation index at the attack and at the end of the decay. */
  index: number;
  index1?: number;
  gain: number;
  a?: number;
  d: number;
  delay?: number;
  f1?: number;
}

/** Two-operator FM: bells, glass, steel, springs. */
export function fm(o: Out, n: FmOpts): number {
  const { ctx } = o;
  const t = o.t + (n.delay ?? 0);
  const a = n.a ?? 0.002;
  const car = ctx.createOscillator();
  const mod = ctx.createOscillator();
  car.frequency.setValueAtTime(n.f, t);
  mod.frequency.setValueAtTime(n.f * n.ratio, t);
  if (n.f1) {
    car.frequency.exponentialRampToValueAtTime(n.f1, t + n.d);
    mod.frequency.exponentialRampToValueAtTime(n.f1 * n.ratio, t + n.d);
  }
  const depth = ctx.createGain();
  const dev = n.f * n.ratio;
  depth.gain.setValueAtTime(n.index * dev, t);
  depth.gain.setTargetAtTime((n.index1 ?? 0) * dev, t + a, n.d / 4);
  mod.connect(depth).connect(car.frequency);
  const g = ctx.createGain();
  perc(g.gain, t, n.gain, a, n.d);
  car.connect(g).connect(o.dest);
  const end = t + a + n.d * 1.3;
  car.start(t);
  mod.start(t);
  car.stop(end + 0.02);
  mod.stop(end + 0.02);
  return end - o.t;
}

/** Ink: a few milliseconds of high-passed noise. */
export const click = (o: Out, gain: number, f = 3500, d = 0.005, delay = 0) => noise(o, { color: "white", type: "highpass", f, gain, a: 0.0004, d, delay });

/** A sine that falls in pitch: kick drums, body hits, sub drops. */
export const thump = (o: Out, gain: number, f = 90, f1 = 40, d = 0.16, delay = 0) => tone(o, { f, f1, glide: d * 0.6, gain, a: 0.0015, d, delay });

/** Air moving past: band-passed pink noise sweeping between two centers. */
export const whoosh = (o: Out, gain: number, f: number, f1: number, d: number, delay = 0, q = 0.9) =>
  noise(o, { color: "pink", type: "bandpass", f, f1, q, gain, a: d * 0.35, d: d * 0.65, delay });

/** Several short metallic clinks scattered over `spread` seconds (chains, junk, debris). */
export function clinks(o: Out, n: number, gain: number, spread: number, lo = 1800, hi = 4200) {
  let end = 0;
  for (let i = 0; i < n; i++) {
    const f = lo + Math.random() * (hi - lo);
    end = Math.max(end, fm(o, { f, ratio: 1.41 + Math.random() * 1.3, index: 2.5, index1: 0.2, gain: gain * (0.5 + Math.random() * 0.5), d: 0.05 + Math.random() * 0.08, delay: Math.random() * spread }));
  }
  return end;
}

// ------------------------------------------------------------------ instruments

/** An instrument plays one note: frequency, held duration and velocity 0..1. */
export type Instrument = (o: Out, f: number, dur: number, vel: number) => number;

function voiceGain(o: Out, t: number, peak: number, a: number, d: number, s: number, hold: number, r: number) {
  const g = o.ctx.createGain();
  adsr(g.gain, t, peak, a, d, s, hold, r);
  g.connect(o.dest);
  return g;
}

function oscs(o: Out, types: OscillatorType[], f: number, detunes: number[], into: AudioNode, t: number, stop: number, scoop = 0) {
  types.forEach((type, i) => {
    const osc = o.ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(f * (1 - scoop), t);
    if (scoop) osc.frequency.exponentialRampToValueAtTime(f, t + 0.05);
    osc.detune.value = detunes[i] ?? 0;
    osc.connect(into);
    osc.start(t);
    osc.stop(stop);
  });
}

/** Brass section: detuned saws, a bright "blat" on the filter, a little scoop into pitch. */
export const brass: Instrument = (o, f, dur, vel) => {
  const t = o.t;
  const g = voiceGain(o, t, 0.16 * vel, 0.03, 0.18, 0.72, dur, 0.22);
  const lp = o.ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.Q.value = 2;
  lp.frequency.setValueAtTime(f * 1.2, t);
  lp.frequency.linearRampToValueAtTime(Math.min(12000, f * (3 + 6 * vel)), t + 0.05);
  lp.frequency.setTargetAtTime(Math.min(9000, f * (2 + 2.5 * vel)), t + 0.06, 0.15);
  lp.connect(g);
  oscs(o, ["sawtooth", "sawtooth", "sawtooth"], f, [-8, 0, 7], lp, t, t + dur + 0.4, 0.012);
  return dur + 0.35;
};

/** Low brass for tanks: one saw into a hard filter and drive. */
export const tuba: Instrument = (o, f, dur, vel) => {
  const t = o.t;
  const d = drive(o, 2.2);
  const g = voiceGain(d, t, 0.3 * vel, 0.02, 0.12, 0.8, dur, 0.18);
  const lp = o.ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(f * 7, t);
  lp.frequency.setTargetAtTime(f * 3.5, t + 0.03, 0.08);
  lp.connect(g);
  oscs(o, ["sawtooth", "square"], f, [0, -5], lp, t, t + dur + 0.3, 0.02);
  return dur + 0.3;
};

/** Synth horn with vibrato and an upward glide into the note (flight). */
export const lead: Instrument = (o, f, dur, vel) => {
  const { ctx } = o;
  const t = o.t;
  const g = voiceGain(o, t, 0.1 * vel, 0.015, 0.1, 0.8, dur, 0.15);
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = Math.min(10000, f * 6);
  lp.Q.value = 1;
  lp.connect(g);
  const vib = ctx.createOscillator();
  vib.frequency.value = 5.6;
  const vibDepth = ctx.createGain();
  vibDepth.gain.setValueAtTime(0, t);
  vibDepth.gain.linearRampToValueAtTime(f * 0.008, t + Math.min(0.35, dur));
  vib.connect(vibDepth);
  for (const [type, det] of [
    ["square", 0],
    ["sawtooth", 6],
  ] as const) {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(f * 0.94, t);
    osc.frequency.exponentialRampToValueAtTime(f, t + 0.06);
    osc.detune.value = det;
    vibDepth.connect(osc.frequency);
    osc.connect(lp);
    osc.start(t);
    osc.stop(t + dur + 0.25);
  }
  vib.start(t);
  vib.stop(t + dur + 0.25);
  return dur + 0.2;
};

/** Glass bell: inharmonic FM plus the octave partial. */
export const bell: Instrument = (o, f, dur, vel) => {
  const ring = dur < 0.1 ? 0.25 + dur * 1.5 : Math.max(0.7, dur * 2);
  fm(o, { f, ratio: 3.5, index: 0.8 + 1.6 * vel, index1: 0.1, gain: 0.1 * vel, d: ring });
  tone(o, { f: f * 2, gain: 0.035 * vel, d: ring * 0.5 });
  return ring * 1.3;
};

/** Marimba-like mallet: quick bright strike, short hollow ring. */
export const mallet: Instrument = (o, f, _dur, vel) => {
  fm(o, { f, ratio: 4, index: 1.4 * vel, index1: 0, gain: 0.14 * vel, d: 0.35 });
  tone(o, { f, gain: 0.08 * vel, d: 0.3 });
  return 0.45;
};

/** "Ah" choir: saws through vowel formants, slow bloom. */
export const choir: Instrument = (o, f, dur, vel) => {
  const { ctx } = o;
  const t = o.t;
  const g = voiceGain(o, t, 0.5 * vel, 0.12, 0.3, 0.85, dur, 0.4);
  for (const [ff, q, lv] of [
    [730, 7, 1],
    [1090, 9, 0.55],
    [2440, 11, 0.3],
  ] as const) {
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = ff;
    bp.Q.value = q;
    const lg = ctx.createGain();
    lg.gain.value = lv;
    bp.connect(lg).connect(g);
    oscs(o, ["sawtooth", "sawtooth"], f, [-10, 9], bp, t, t + dur + 0.6);
  }
  return dur + 0.5;
};

/** Breathy flute: sine with a puff of air at the front. */
export const flute: Instrument = (o, f, dur, vel) => {
  const t = o.t;
  const g = voiceGain(o, t, 0.12 * vel, 0.04, 0.2, 0.8, dur, 0.18);
  oscs(o, ["sine", "triangle"], f, [0, 3], g, t, t + dur + 0.3);
  noise(o, { color: "pink", type: "bandpass", f: f * 2, q: 3, gain: 0.03 * vel, a: 0.02, d: 0.12 });
  return dur + 0.25;
};

/** Plucked saw for ostinatos. */
export const pluck: Instrument = (o, f, dur, vel) => {
  const t = o.t;
  const g = o.ctx.createGain();
  perc(g.gain, t, 0.12 * vel, 0.002, Math.max(0.12, dur));
  g.connect(o.dest);
  const lp = o.ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.Q.value = 4;
  lp.frequency.setValueAtTime(f * 10, t);
  lp.frequency.setTargetAtTime(f * 1.6, t + 0.002, 0.05);
  lp.connect(g);
  oscs(o, ["sawtooth"], f, [0], lp, t, t + Math.max(0.12, dur) * 1.3 + 0.05);
  return Math.max(0.12, dur) * 1.3;
};

export const sub: Instrument = (o, f, dur, vel) => {
  const g = voiceGain(o, o.t, 0.35 * vel, 0.01, 0.2, 0.9, dur, 0.2);
  oscs(o, ["sine"], f, [0], g, o.t, o.t + dur + 0.3);
  return dur + 0.25;
};

// ------------------------------------------------------------------ percussion

export const timpani = (o: Out, f: number, vel: number) => {
  tone(o, { f: f * 1.02, f1: f, glide: 0.2, gain: 0.45 * vel, d: 1.1 });
  tone(o, { f: f * 1.5, gain: 0.08 * vel, d: 0.5 });
  noise(o, { color: "brown", type: "lowpass", f: 400, gain: 0.35 * vel, d: 0.2 });
  return 1.4;
};

export function cymbal(o: Out, vel: number, dur = 1.6, swell = false) {
  const { ctx } = o;
  const t = o.t;
  const g = ctx.createGain();
  if (swell) {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.1 * vel, t + dur);
    g.gain.setTargetAtTime(0, t + dur, 0.02);
  } else perc(g.gain, t, 0.09 * vel, 0.002, dur);
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 5200;
  hp.connect(g).connect(o.dest);
  // Six detuned squares at inharmonic ratios: the classic metal-plate spectrum.
  for (const r of [1, 1.342, 1.2312, 1.6532, 1.9523, 2.1523]) {
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.value = 410 * r;
    osc.connect(hp);
    osc.start(t);
    osc.stop(t + dur * 1.3 + 0.05);
  }
  noise({ ...o, t }, { color: "white", type: "highpass", f: 7000, gain: swell ? 0 : 0.12 * vel, d: dur * 0.6 });
  return dur * 1.3;
}

export const kick = (o: Out, vel: number) => {
  thump(o, 0.7 * vel, 150, 45, 0.3);
  click(o, 0.15 * vel, 2500, 0.004);
  return 0.4;
};

export const snare = (o: Out, vel: number) => {
  noise(o, { color: "white", type: "bandpass", f: 2200, q: 0.6, gain: 0.35 * vel, d: 0.16 });
  tone(o, { f: 196, f1: 170, gain: 0.25 * vel, d: 0.09 });
  return 0.25;
};

export const hat = (o: Out, vel: number) => noise(o, { color: "white", type: "highpass", f: 8000, gain: 0.12 * vel, d: 0.04 });

export const wood = (o: Out, f: number, vel: number) => fm(o, { f, ratio: 2.3, index: 1.2, index1: 0, gain: 0.2 * vel, d: 0.06 });
